import { Checkbox, FormControlLabel, Grid, Typography, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';

import { useFormikContext } from 'formik';
import {
  FormPrintReceiptPos,
  PrintReceiptPosBody,
} from 'front-office/view-models/print-receipt-pos-page';
import { Fragment, memo, useEffect, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from 'front-office/constants/print-receipt-pos-head-cells';
import { noop } from 'shell/utils/noop';

interface TablePrintReceiptPosProps {
  data?: FormPrintReceiptPos;
}

const TablePrintReceiptPosComponent = ({ data }: TablePrintReceiptPosProps): React.ReactElement => {
  const formContext = useFormikContext<FormPrintReceiptPos>();

  useEffect(() => {
    if (!data) {
      return;
    }

    formContext.setValues(data);
  }, [data]);

  const handleChangePage = useEventCallback((newPage: number) => {
    formContext.setFieldValue('page', newPage);
  });

  const handleChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    const newDataset = {
      ...formContext.values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    };
    formContext.setValues(newDataset);
  });

  const onChangeCheckbox = useEventCallback((item: PrintReceiptPosBody) => {
    if (formContext.values?.selected?.find((el) => el.number === item.number)) {
      // già inserito

      const newSelected = formContext.values?.selected?.filter((el) => el.number !== item.number);

      formContext.setFieldValue('selected', newSelected || []);
      return;
    }
    // da inserire

    const newSelected = (formContext.values?.selected || []).slice();

    newSelected.push(item);

    formContext.setFieldValue('selected', newSelected || []);
  });

  const body: Array<PrintReceiptPosBody> = useMemo(() => {
    if (!formContext.values?.list) {
      return [];
    }

    return formContext.values?.list.map((item, index) => {
      return {
        ...item,
        action: (
          <FormControlLabel
            control={
              <Checkbox
                id={`isSelected-${index}`}
                checked={
                  formContext.values?.selected?.find((el) => el.number === item.number)
                    ? true
                    : false
                }
                onChange={() => onChangeCheckbox(item)}
                inputProps={{ 'aria-label': 'Seleziona riga' }}
              />
            }
            label=""
          />
        ),
      } as PrintReceiptPosBody;
    }) as Array<PrintReceiptPosBody>;
  }, [formContext.values?.list, formContext.values?.selected]);

  return !body?.length ? (
    <>
      <Typography color="secondary.main">
        Nessun elemento trovato con la ricerca effettuata.
      </Typography>
    </>
  ) : (
    <Fragment>
      <Table
        onSort={noop}
        noSort
        head={headCells}
        orders={{}}
        page={formContext.values.page || 0}
        rowsPerPage={formContext.values.rowsPerPage}
        body={body}
        count={(body || [])?.length || 0}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ariaLabel="Lista certificati"
        hideButtons
      />
      <Grid item container spacing={2} xs={12} justifyContent="flex-end" display="flex">
        <Grid item xs={12} md={2}>
          <PmiButton variant="contained" color="secondary" fullWidth type="submit">
            Stampa
          </PmiButton>
        </Grid>
      </Grid>
      {formContext.touched.selected && Boolean(formContext.errors.selected) ? (
        <div style={{ padding: '16px 0 0 0', color: '#d32f2f' }}>{formContext.errors.selected}</div>
      ) : undefined}
    </Fragment>
  );
};

TablePrintReceiptPosComponent.displayName = 'Table Print Receipt Pos';

const TablePrintReceiptPos = memo(TablePrintReceiptPosComponent);

export default TablePrintReceiptPos;
